import React, { useMemo, useState } from 'react';
import { Dropdown, Slider, InputNumber, Tooltip } from 'antd';
import { ClockCircleOutlined, BgColorsOutlined, UploadOutlined, AppstoreOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MapIconsService, { RecentIconItem } from '../../../../../services/MapIconService/mapIcon.service';
import { QueryKeys } from '../../../../../enums/queryKeys';
import { BUILT_IN_SHAPES, isBuiltInShape, findBuiltInShape } from './builtInShapes';
import { SymbologyIcon } from '../../../../../types/symbology.types';
import '../../LayerProperties.scss';
import './IconControls.scss';

// US-101662 Scenario 6: rotation must be between 0 and 360 degrees inclusive.
const ROTATION_ERROR = 'Rotation must be between 0 and 360 degrees';
const isValidRotation = (n: number | null | undefined) => n == null || (Number.isFinite(n) && n >= 0 && n <= 360);

interface ControlledIconPickerProps {
  /** Current icon, or null for "no icon". */
  value: SymbologyIcon | null;
  onChange: (icon: SymbologyIcon | null) => void;
  /**
   * Open the shared "Choose from styles" modal or the Upload flow and resolve
   * with the chosen icon (or null if cancelled). Implemented by the parent so
   * the heavy presign/upload/modal plumbing isn't duplicated per editor.
   */
  onRequestExternalIcon: (source: 'styles' | 'upload') => Promise<{ key: string } | null>;
}

/**
 * A fully controlled version of IconControls: it operates on a SymbologyIcon
 * object (not the global LayerProperties context), so it can drive a single
 * unique-category's icon inside a local draft. Built-in shapes and recently
 * used icons are picked inline; "Choose from styles" / "Upload" delegate to
 * the parent and update the value when they resolve.
 */
const ControlledIconPicker: React.FC<ControlledIconPickerProps> = ({ value, onChange, onRequestExternalIcon }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [submenu, setSubmenu] = useState<'none' | 'recent' | 'custom'>('none');
  const { getRecentIcons, fetchIconUrl, touchRecentIcon } = MapIconsService();
  const { mutate: touchRecentIconMutate } = useMutation(touchRecentIcon());
  const queryClient = useQueryClient();
  const { data: recentIcons } = useQuery({ ...getRecentIcons(), enabled: dropdownOpen, staleTime: 60_000 });

  const size = value?.size ?? 0;
  const rotation = value?.rotation ?? 0;
  const selectedKey = value?.key;

  // Resolve the uploaded image to a presigned URL so the trigger shows the
  // actual icon thumbnail instead of the literal word "Image".
  const isImageKey = !!selectedKey && !isBuiltInShape(selectedKey);
  const { data: triggerIconUrl } = useQuery(fetchIconUrl(isImageKey ? selectedKey : undefined));

  const triggerPreview = useMemo(() => {
    if (!selectedKey) return null;
    const shape = findBuiltInShape(selectedKey);
    if (shape) {
      return (
        <svg viewBox="0 0 24 24" className="icon-controls__trigger-svg" aria-hidden>
          <path d={shape.path} />
        </svg>
      );
    }
    if (triggerIconUrl) {
      return <img src={triggerIconUrl} className="icon-controls__trigger-img" alt="" />;
    }
    return <span className="icon-controls__trigger-label">Image</span>;
  }, [selectedKey, triggerIconUrl]);

  const closeDropdown = () => {
    setDropdownOpen(false);
    setSubmenu('none');
  };

  const pickKey = (key: string) => {
    const isShape = isBuiltInShape(key);
    onChange({
      type: isShape ? 'shape' : 'recent',
      key,
      size: size || (isShape ? 12 : 24),
      rotation,
      uploadKind: isShape ? 'shape' : 'image',
    });
    // Record so the pick appears under "Recently used".
    touchRecentIconMutate({ key });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_RECENT_ICONS] });
    closeDropdown();
  };

  const pickExternal = async (source: 'styles' | 'upload') => {
    closeDropdown();
    const result = await onRequestExternalIcon(source);
    if (!result) return;
    onChange({
      type: source === 'styles' ? 'styles' : 'upload',
      key: result.key,
      size: size || 24,
      rotation,
      uploadKind: 'image',
    });
  };

  const renderDropdownContent = () => {
    if (submenu === 'recent') {
      return (
        <div className="icon-controls__panel">
          <button type="button" className="icon-controls__panel-back" onClick={() => setSubmenu('none')}>
            <LeftOutlined />
            <span>Recently used</span>
          </button>
          <div className="icon-controls__grid">
            {(recentIcons ?? []).map((r: RecentIconItem) => (
              <button
                key={r.iconKey}
                type="button"
                className={`icon-controls__grid-item${selectedKey === r.iconKey ? ' icon-controls__grid-item--active' : ''}`}
                onClick={() => pickKey(r.iconKey)}
                title={r.iconKey}
              >
                {isBuiltInShape(r.iconKey) ? (
                  <svg viewBox="0 0 24 24" className="icon-controls__grid-svg">
                    <path d={findBuiltInShape(r.iconKey)!.path} />
                  </svg>
                ) : (
                  <img src={r.url} alt="" className="icon-controls__grid-img" />
                )}
              </button>
            ))}
            {(!recentIcons || recentIcons.length === 0) && <div className="icon-controls__panel-empty">No recently used icons yet.</div>}
          </div>
        </div>
      );
    }
    if (submenu === 'custom') {
      return (
        <div className="icon-controls__panel">
          <button type="button" className="icon-controls__panel-back" onClick={() => setSubmenu('none')}>
            <LeftOutlined />
            <span>Custom icon</span>
          </button>
          <div className="icon-controls__grid">
            {BUILT_IN_SHAPES.map((shape) => (
              <button
                key={shape.key}
                type="button"
                className={`icon-controls__grid-item${selectedKey === shape.key ? ' icon-controls__grid-item--active' : ''}`}
                onClick={() => pickKey(shape.key)}
                title={shape.label}
              >
                <svg viewBox="0 0 24 24" className="icon-controls__grid-svg">
                  <path d={shape.path} />
                </svg>
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="icon-controls__menu">
        <button type="button" className="icon-controls__menu-item" onClick={() => setSubmenu('recent')}>
          <ClockCircleOutlined />
          <span>Recently used</span>
          <RightOutlined className="icon-controls__menu-chevron" />
        </button>
        <button type="button" className="icon-controls__menu-item" onClick={() => pickExternal('styles')}>
          <BgColorsOutlined />
          <span>Choose from styles</span>
        </button>
        <button type="button" className="icon-controls__menu-item" onClick={() => pickExternal('upload')}>
          <UploadOutlined />
          <span>Upload</span>
        </button>
        <button type="button" className="icon-controls__menu-item" onClick={() => setSubmenu('custom')}>
          <AppstoreOutlined />
          <span>Custom icon</span>
          <RightOutlined className="icon-controls__menu-chevron" />
        </button>
      </div>
    );
  };

  const setSize = (n: number) =>
    onChange(value ? { ...value, size: n } : { type: 'shape', key: 'shape:circle', size: n, rotation, uploadKind: 'shape' });
  const setRotation = (n: number) =>
    onChange(value ? { ...value, rotation: n } : { type: 'shape', key: 'shape:circle', size, rotation: n, uploadKind: 'shape' });
  const rotationInvalid = !isValidRotation(rotation);

  return (
    <>
      <div className="icon-controls__group">
        <div className="icon-controls__group-label">ICON</div>
        <Dropdown
          trigger={['click']}
          open={dropdownOpen}
          onOpenChange={(next) => {
            setDropdownOpen(next);
            if (!next) setSubmenu('none');
          }}
          placement="bottomLeft"
          dropdownRender={() => renderDropdownContent()}
        >
          <button type="button" className="icon-controls__trigger">
            <span className="icon-controls__trigger-preview">{triggerPreview}</span>
            <RightOutlined rotate={90} className="icon-controls__trigger-caret" />
          </button>
        </Dropdown>
      </div>

      <div className="icon-controls__group">
        <div className="icon-controls__group-label">SIZE</div>
        <div className="icon-controls__group-row">
          <Slider className="icon-controls__slider" value={size} min={0} max={100} onChange={setSize} />
          <InputNumber
            className="icon-controls__num-input"
            value={size}
            min={0}
            max={100}
            onChange={(v) => setSize(typeof v === 'number' ? v : 0)}
            formatter={(v) => (v == null ? '' : `${v}pt`)}
            parser={(v) => (v ? Number(String(v).replace('pt', '')) : 0) as 0}
          />
        </div>
      </div>

      <div className="icon-controls__group">
        <div className="icon-controls__group-label">ICON ROTATION</div>
        <div className="icon-controls__group-row">
          <Slider className="icon-controls__slider" min={0} max={360} value={rotation} onChange={setRotation} />
          <Tooltip title={rotationInvalid ? ROTATION_ERROR : undefined} open={rotationInvalid ? true : false}>
            <InputNumber
              className="icon-controls__num-input"
              value={rotation}
              min={0}
              max={360}
              status={rotationInvalid ? 'error' : undefined}
              onChange={(v) => setRotation(typeof v === 'number' ? v : 0)}
              formatter={(v) => (v == null ? '' : `${v}°`)}
              parser={(v) => (v ? Number(String(v).replace('°', '')) : 0) as 0}
              placeholder="°"
            />
          </Tooltip>
        </div>
        {rotationInvalid && <div className="icon-controls__rotation-error">{ROTATION_ERROR}</div>}
      </div>
    </>
  );
};

export default ControlledIconPicker;
